/**
 * Bounded Context Command Service
 * 
 * Application service for bounded context write operations following CQRS pattern.
 * Encapsulates business use cases and orchestrates domain operations.
 */

import { injectable, inject } from 'inversify';
import { BoundedContext, BoundedContextProperties } from '../../domain/entities/BoundedContext';
import type { IBoundedContextRepository } from '../../../meta-model-design/domain/ports/IBoundedContextRepository';
import type { IDomainRepository } from '../../../meta-model-design/domain/ports/IDomainRepository';
import { IEventBus } from '@swoft/core';
import { BoundedContextCreatedEvent } from '../../domain/events/BoundedContextCreatedEvent';
import { CreateBoundedContextPayload } from '../commands/CreateBoundedContextCommand';
import { DDD_SYMBOLS } from '../../../../infrastructure/di/DDD_SYMBOLS';

export interface CreateBoundedContextResult {
  boundedContextId: string;
  success: boolean;
  message: string;
}

@injectable()
export class BoundedContextCommandService {
  constructor(
    @inject(DDD_SYMBOLS.IBoundedContextRepository) private boundedContextRepository: IBoundedContextRepository,
    @inject(DDD_SYMBOLS.IDomainRepository) private domainRepository: IDomainRepository,
    @inject(DDD_SYMBOLS.IEventBus) private eventBus: IEventBus
  ) {}

  async createBoundedContext(
    domainId: string,
    payload: CreateBoundedContextPayload,
    authSessionId: string,
    initiatedByPersonId: string,
    correlationId?: string
  ): Promise<CreateBoundedContextResult> {
    // Verify domain exists
    const domain = await this.domainRepository.findById(domainId);
    if (!domain) {
      throw new Error(`Domain with ID ${domainId} not found`);
    }

    // Check if context name already exists in this domain
    const existingContext = await this.boundedContextRepository.findByName(
      domainId,
      payload.name
    );
    if (existingContext) {
      throw new Error(`Bounded context '${payload.name}' already exists in this domain`);
    }

    // Transform input arrays to DomainModelElement with generated IDs
    const domainServices = (payload.domainServices || []).map(service => ({
      id: crypto.randomUUID(),
      name: service.name,
      description: service.description
    }));

    const aggregates = (payload.aggregates || []).map(aggregate => ({
      id: crypto.randomUUID(),
      name: aggregate.name,
      description: aggregate.description
    }));

    const valueObjects = (payload.valueObjects || []).map(vo => ({
      id: crypto.randomUUID(),
      name: vo.name,
      description: vo.description
    }));

    const entities = (payload.entities || []).map(entity => ({
      id: crypto.randomUUID(),
      name: entity.name,
      description: entity.description
    }));

    // Create bounded context entity with business logic validation
    const boundedContext = payload.id 
      ? BoundedContext.reconstitute({
          domainId: domainId,
          name: payload.name,
          description: payload.description,
          type: payload.type || 'core', // Use core type for explicitly specified contexts
          ubiquitousLanguage: payload.ubiquitousLanguage || {},
          domainServices,
          aggregates,
          valueObjects,
          entities
        }, payload.id, new Date(), new Date())
      : BoundedContext.create({
          domainId: domainId,
          name: payload.name,
          description: payload.description,
          type: payload.type || 'supporting', // Default to supporting for auto-generated contexts
          ubiquitousLanguage: payload.ubiquitousLanguage || {},
          domainServices,
          aggregates,
          valueObjects,
          entities
        });

    // Persist to repository
    await this.boundedContextRepository.save(boundedContext);

    // Publish bounded context created event
    const boundedContextCreatedEvent = new BoundedContextCreatedEvent(
      boundedContext.id,
      boundedContext.domainId,
      boundedContext.name,
      boundedContext.type,
      authSessionId,
      initiatedByPersonId,
      correlationId
    );

    await this.eventBus.publish(boundedContextCreatedEvent);

    return {
      boundedContextId: boundedContext.id,
      success: true,
      message: `Bounded context "${payload.name}" created successfully with ID ${boundedContext.id}`
    };
  }

  async deleteBoundedContext(boundedContextId: string): Promise<void> {
    const exists = !!await this.boundedContextRepository.findById(boundedContextId);
    if (!exists) {
      throw new Error(`Bounded context not found: ${boundedContextId}`);
    }

    await this.boundedContextRepository.deleteById(boundedContextId);
  }
}